import { useIsMutationPending, useMutation } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import useLatest from "@-/com.npmjs.use-latest";
import { watchAsyncStep } from "@-/dev.improve.haniverse.lib.async-watchdog";
import {
  releaseRatingLatch,
  tryAcquireRatingLatch,
} from "@-/dev.improve.haniverse.lib.cards.review-latch";
import type {
  LearnCardAction,
  LearnCardActionReceipt,
  LearnCardActionSnapshot,
} from "@-/dev.improve.haniverse.lib.learn.card-action";
import type {
  LearnCardDestinationOrigin,
  LearnCardDestinationResolution,
} from "@-/dev.improve.haniverse.lib.learn.destination";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { useDailyNewCardsSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.daily-new-cards.navigator";
import { useCallback, useMemo } from "react";
import {
  type LearnCardNavigationMode,
  useNavigateToLearnCardDestination,
} from "#use-navigate-to-learn-card-destination";
import { useResolveLearnCardDestination } from "#use-resolve-learn-card-destination";
import { useLearnCardActionPersistence } from "#use-learn-card-action-persistence";

export type { LearnCardNavigationMode } from "#use-navigate-to-learn-card-destination";

/**
 * Shared by every controller submit so any surface can observe an in-flight action without
 * owning the mutation — the floating rating buttons disable themselves while a swipe's rating is
 * saving, and vice versa.
 */
export const LEARN_CARD_ACTION_MUTATION_KEY = ["learnCard", "action"] as const;

/**
 * The whole persist → resolve → navigate pipeline, bounded where the latch is held. The query
 * client bounds the mutation function too, but that backstop only releases the AWAITER: the
 * `finally` below would still wait on a step that never settles, holding the rating latch — and
 * with it every rating and undo — forever. Above the page navigator’s own preparation deadline
 * (30s), so a slow destination fails with its specific error first; below the 60s mutation
 * backstop, so the latch is released by this pipeline rather than never.
 */
const LEARN_CARD_ACTION_DEADLINE_MS = 45_000;

export function useIsLearnCardActionPending(): boolean {
  return useIsMutationPending(LEARN_CARD_ACTION_MUTATION_KEY);
}

export type LearnCardActionSubmitResult =
  /** Another action already held the latch; nothing was written and nothing moved. */
  | { status: "busy" }
  /**
   * The action was refused before any write — today only an enrollment the free-tier gate
   * blocked. The paywall has been presented; the learner stays on the card.
   */
  | { receipt: LearnCardActionReceipt; status: "blocked" }
  /** Written and advanced. */
  | {
      receipt: LearnCardActionReceipt;
      resolution: LearnCardDestinationResolution;
      status: "advanced";
    };

/**
 * The single pipeline every Learn card action goes through — buttons, swipes, shortcut keys, voice
 * commands, fast-forward. Three steps, each usable on its own by the deck and composed by
 * {@link submit} for route-driven callers:
 *
 *   1. `persist` — write the action for a frozen snapshot (`executeLearnCardAction`), then apply
 *      the receipt's effects: word-unlock reconciliation for graphemes that crossed Familiar, and
 *      the undo entry for a review that landed.
 *   2. `resolve` — the authoritative successor from post-write state.
 *   3. `navigate` — go there.
 *
 * `submit` holds the shared rating latch across all three, exactly as the old rating mutations
 * did: a second press while a rating is saving and the next card is loading is dropped, and undo
 * cannot interleave with a rating. The latch is the fast, in-process guard; the durable one is the
 * operation id the review rows are named after.
 */
export function useLearnCardActionController() {
  // This pipeline owns navigation, so it hands persistence the one route enrollment can offer.
  const dailyNewCardsSettingsPageNavigator = useDailyNewCardsSettingsPageNavigator();
  const { originFor, persist } = useLearnCardActionPersistence({
    openDailyNewCardsSettings: () => dailyNewCardsSettingsPageNavigator.push.mutate(),
  });
  const [setHasLearnFlowStartedThisSession] = useContextSelectors(
    NavContext,
    (c) => c.setHasLearnFlowStartedThisSession,
  );
  const setHasLearnFlowStartedThisSessionRef = useLatest(setHasLearnFlowStartedThisSession);
  const resolve = useResolveLearnCardDestination();
  const navigate = useNavigateToLearnCardDestination();

  const advance = useCallback(
    async ({
      mode,
      origin,
    }: {
      mode: LearnCardNavigationMode;
      origin: LearnCardDestinationOrigin;
    }): Promise<LearnCardDestinationResolution> => {
      if (origin.kind === "review") {
        // Reaching here means the launch-only Learn summary greeting has been consumed, even when
        // the review route was opened directly rather than from the summary CTA.
        setHasLearnFlowStartedThisSessionRef.current(true);
      }
      const resolution = await watchAsyncStep("learnCard: resolveDestination", () =>
        resolve(origin, { committed: true }),
      );
      await watchAsyncStep("learnCard: navigate", () => navigate(resolution, { mode }));
      return resolution;
    },
    [navigate, resolve, setHasLearnFlowStartedThisSessionRef],
  );

  const submitMutation = useMutation({
    mutationFn: async ({
      action,
      mode,
      operationId,
      snapshot,
    }: {
      action: LearnCardAction;
      mode: LearnCardNavigationMode;
      operationId?: string;
      snapshot: LearnCardActionSnapshot;
    }): Promise<LearnCardActionSubmitResult> => {
      const latch = tryAcquireRatingLatch();
      if (latch === null) {
        return { status: "busy" };
      }
      try {
        // A straggler that lands after the deadline is harmless: its review rows are named after
        // the operation id, so a retry of the same operation finds them instead of rating twice.
        return await withDeadline(
          { deadline: LEARN_CARD_ACTION_DEADLINE_MS, operation: "Learn card action" },
          async (): Promise<LearnCardActionSubmitResult> => {
            const receipt = await persist({ action, operationId, snapshot });
            if (receipt.persisted.kind === "enrollment" && !receipt.persisted.wasAdded) {
              // Past the free tier: the gate has presented the paywall. Stay put rather than
              // advancing into a card that assumes the enrollment happened.
              return { receipt, status: "blocked" };
            }
            const resolution = await advance({ mode, origin: originFor(snapshot) });
            return { receipt, resolution, status: "advanced" };
          },
        );
      } finally {
        releaseRatingLatch(latch);
      }
    },
    mutationKey: LEARN_CARD_ACTION_MUTATION_KEY,
    onError: (error) => {
      logger(import.meta.location).error("Learn card action failed", error);
    },
  });

  const submit = useCallback(
    (variables: Parameters<typeof submitMutation.mutateAsync>[0]) =>
      submitMutation.mutateAsync(variables),
    [submitMutation],
  );

  return useMemo(
    () => ({ advance, originFor, persist, submit, submitMutation }),
    [advance, originFor, persist, submit, submitMutation],
  );
}
